export type DraftConfigUpdatedPayload = {
  checksum?: unknown
  message?: string | null
  tabId?: string | null
}

export type RemoteRefetchState = {
  hasUnsavedLocalChanges: boolean
  isLocalEditActive: boolean
  isSaving: boolean
}

export type RemoteRefetchCoordinator = {
  request: () => boolean
  flush: () => boolean
  cancel: () => boolean
  isDeferred: () => boolean
}

export function usableChecksum(checksum: unknown): string | null {
  return typeof checksum === 'string' && checksum.length > 0 ? checksum : null
}

export function isOwnDraftConfigEvent(
  payload: DraftConfigUpdatedPayload,
  currentTabId: string | undefined
): boolean {
  return !!currentTabId && payload.tabId === currentTabId
}

export function shouldDeferRemoteRefetch(state: RemoteRefetchState): boolean {
  return state.hasUnsavedLocalChanges || state.isLocalEditActive || state.isSaving
}

export function shouldRequestRemoteRefetch(args: {
  checksum: unknown
  lastChecksum: string | null
  autoRefreshEnabled: boolean
}): boolean {
  if (!args.autoRefreshEnabled) return false

  const checksum = usableChecksum(args.checksum)
  if (!checksum) return true

  return checksum !== args.lastChecksum
}

/**
 * The single rule that replaces the old "wholesale clobber OR three-way merge"
 * fork (ENG-1066). When the server's draft has moved ahead of us — either a
 * foreign `DraftConfigUpdated` (MCP/Righty/another tab) arrived, or the backend
 * rejected our save as stale — we never algorithmically merge and never
 * re-POST stale local state. We only ever:
 *
 *  - `'refresh'`: there is nothing local to lose, so refetch and adopt the
 *    server/MCP state wholesale. This alone fixes the dominant data-loss
 *    report: a backgrounded webapp with no edits adopts the MCP change instead
 *    of autosaving its pre-MCP copy over it.
 *  - `'prompt'`: there are unsaved local edits, so DON'T touch them and DON'T
 *    save over the newer server state — defer the refetch and surface a
 *    non-blocking "reload to see the latest" prompt. Concurrent edits resolve
 *    by reload, which is the acknowledged non-goal boundary (no OT/CRDT).
 */
export function resolveStaleOrForeignChange(args: {
  hasUnsavedLocalChanges: boolean
}): 'refresh' | 'prompt' {
  return args.hasUnsavedLocalChanges ? 'prompt' : 'refresh'
}

// Decide whether a deep-watcher config mutation should trigger the debounced
// autosave. A mutation must NOT be persisted as a draft when it did not
// originate from a user edit:
//  - hasMeaningfulChanges=false: the diff is only passive metadata churn such as
//    `accessedAt`. Opening a flow / segment group / widget must not write the
//    whole config back to the server or mark the tab as locally editing.
//  - isApplyingRemoteConfig: the watchEffect is adopting a server/MCP config
//    wholesale after a refetch — saving it back would bounce the server state
//    we just adopted (ENG-1066; previously the three-way-merge `isMerging`
//    guard, ENG-966).
//  - isNormalizing: a passive normalization triggered purely by opening the
//    UI — load-time repairs like `repairSegmentGroup`, `repairQuestions`, and
//    `repairFlowSplits`. Without this guard, merely viewing an editor can queue
//    a spurious unpublished change (ENG-1063 / startup autosave race).
export function shouldAutosaveConfigChange(args: {
  hasMeaningfulChanges: boolean
  isApplyingRemoteConfig: boolean
  isNormalizing: boolean
}): boolean {
  return args.hasMeaningfulChanges && !args.isApplyingRemoteConfig && !args.isNormalizing
}

export function createRemoteRefetchCoordinator(args: {
  shouldHold: () => boolean
  refetch: () => void
}): RemoteRefetchCoordinator {
  let deferred = false

  return {
    request() {
      if (args.shouldHold()) {
        deferred = true
        return false
      }

      deferred = false
      args.refetch()
      return true
    },
    flush() {
      if (!deferred || args.shouldHold()) {
        return false
      }

      deferred = false
      args.refetch()
      return true
    },
    cancel() {
      if (!deferred) {
        return false
      }

      deferred = false
      return true
    },
    isDeferred() {
      return deferred
    }
  }
}
